import api from '../utils/api.js';
import { userFacingDetail } from '../utils/errorHandler.js';

/**
 * 求人プール (SS-01, FR-MATCH-008) API 呼び出し集約層
 *
 * エンドポイントは backend/app/routers/job_pool.py (Issue #412, PR #110) 準拠。
 * AI マッチング (SS-02) 側の API 呼び出しは services/matchingApi.js
 * (Issue #413, feature/413-ai-matching-ui) に別途実装されている。
 * 両ファイルの統合要否は carry-over Issue #471 で対応予定。
 */

const PDF_CONTENT_TYPE = 'application/pdf';

/**
 * 求人ファイルの Content-Type を判定する (画面レイアウト_SCR-M-001.md §4.1 IN-M-001)。
 * 受け付けるのは PDF のみ。CSV は第12回定期協議会 (2026-09-16) の決定で対象外とした (Issue #1050)。
 * File.type はブラウザの判定に依存し空文字になる場合があるため、拡張子を優先して判定する。
 *
 * @param {File} file
 * @returns {string|null} 対応形式でない場合は null
 */
export function detectJobPoolContentType(file) {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return PDF_CONTENT_TYPE;
    // 拡張子で判定できない場合は File.type にフォールバック
    if (file.type === PDF_CONTENT_TYPE) return PDF_CONTENT_TYPE;
    return null;
}

// 取込エラーの文言 (画面レイアウト_SCR-M-001.md §6.3「求人ファイルの取込エラー」の No. と対応)
export const UPLOAD_ERROR_MESSAGES = {
    loginRequired: 'ログインが必要です。', // No.1
    uploadUrlRejected: '署名付きURLの取得に失敗しました。', // No.2 (detail が無い場合)
    uploadUrlFailed: 'アップロードの準備に失敗しました。時間をおいて再試行してください。', // No.3
    gcsUploadFailed: 'ファイルのアップロードに失敗しました。通信状況を確認して再試行してください。', // No.4
    confirmRejected: '求人ファイルを取り込めませんでした。ファイルの内容をご確認ください。', // No.5 (detail が無い場合)
    // No.7。「管理者にお問い合わせください」は書かない（問い合わせの方法も運用も決まっていないため。#2379 で丹羽様のご判断）
    confirmServerError:
        '求人ファイルを取り込めませんでした（サーバーでエラーが発生しました）。もう一度お試しください。'
        + '同じファイルで繰り返し失敗する場合は、求人票の内容が原因のことがあり、再試行しても取り込めません。'
        + '再試行すると AI ポイントを消費する場合があります。',
    confirmConnectionLost:
        '通信が途中で終了しました。取り込まれている場合があります。'
        + '求人プール一覧に表示されているかを確認してから、必要に応じて再試行してください。', // No.8
};

const failure = (errorMessage, retryable) => ({ ok: false, errorMessage, retryable });

/** 応答本文を JSON として読む。JSON でない (ロードバランサの HTML 応答等) 場合は null。 */
async function readJsonOrNull(response) {
    try {
        return await response.json();
    } catch {
        return null;
    }
}

/**
 * 求人プールファイルのアップロード用署名付きURLを発行する (T-001-U1)。
 * 失敗時の文言と再試行の可否は SCR-M-001 §6.3 No.1〜3。
 *
 * @param {string} contentType - 'application/pdf'
 * @returns {Promise<{ok: boolean, uploadUrl?: string, blobName?: string, errorMessage?: string, retryable?: boolean}>}
 */
export async function getJobPoolUploadUrl(contentType) {
    let response;
    try {
        response = await api.fetch(
            `/api/matching/job-pool/upload_url?content_type=${encodeURIComponent(contentType)}`
        );
    } catch {
        return failure(UPLOAD_ERROR_MESSAGES.uploadUrlFailed, true);
    }

    if (!response) {
        return failure(UPLOAD_ERROR_MESSAGES.loginRequired, false);
    }

    if (!response.ok) {
        if (response.status >= 400 && response.status < 500) {
            const errData = await readJsonOrNull(response);
            return failure(userFacingDetail(response.status, errData) || UPLOAD_ERROR_MESSAGES.uploadUrlRejected, false);
        }
        return failure(UPLOAD_ERROR_MESSAGES.uploadUrlFailed, true);
    }

    // 成功応答でも本文が読めなければ、まだ何も送信していないため No.3 とする
    const data = await readJsonOrNull(response);
    if (!data?.upload_url) {
        return failure(UPLOAD_ERROR_MESSAGES.uploadUrlFailed, true);
    }
    return { ok: true, uploadUrl: data.upload_url, blobName: data.blob_name };
}

/**
 * 発行済みの署名付きURLへファイル本体をPUTアップロードする。
 * 失敗は応答の状態を問わず再試行可 (SCR-M-001 §6.3 No.4。再試行は URL 発行からやり直す)。
 *
 * @param {string} uploadUrl
 * @param {File} file
 * @param {string} contentType
 * @returns {Promise<{ok: boolean, errorMessage?: string, retryable?: boolean}>}
 */
export async function uploadJobPoolFile(uploadUrl, file, contentType) {
    try {
        const response = await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': contentType },
            body: file,
        });
        if (!response.ok) {
            return failure(UPLOAD_ERROR_MESSAGES.gcsUploadFailed, true);
        }
        return { ok: true };
    } catch {
        return failure(UPLOAD_ERROR_MESSAGES.gcsUploadFailed, true);
    }
}

/** 取込確定の失敗応答を SCR-M-001 §6.3 No.5〜8 に振り分ける。 */
async function classifyConfirmFailure(response) {
    const errData = await readJsonOrNull(response);
    if (errData === null) {
        // サーバで取込が済んでいる場合がある (No.8)
        return failure(UPLOAD_ERROR_MESSAGES.confirmConnectionLost, true);
    }
    if (response.status >= 400 && response.status < 500) {
        return failure(userFacingDetail(response.status, errData) || UPLOAD_ERROR_MESSAGES.confirmRejected, false);
    }
    if (response.status === 502 && errData.detail) {
        return failure(errData.detail, true);
    }
    // サーバの「予期しないエラーが発生しました。」はそのまま出さない (No.7)
    return failure(UPLOAD_ERROR_MESSAGES.confirmServerError, true);
}

/**
 * 求人プールファイルのアップロード完了を確認し、Gemini抽出・重複判定・蓄積をトリガーする
 * (T-001-U1、backend/app/routers/job_pool.py::confirm_job_pool_upload)。
 * 失敗時の文言と再試行の可否は SCR-M-001 §6.3 No.1・No.5〜8。
 *
 * @param {{blobName: string, fileName: string, targetCandidateId: string}} params
 * @returns {Promise<{ok: boolean, data?: object, errorMessage?: string, retryable?: boolean}>}
 */
export async function confirmJobPoolUpload({ blobName, fileName, targetCandidateId }) {
    let response;
    try {
        response = await api.fetch('/api/matching/job-pool/confirm', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                blob_name: blobName,
                file_name: fileName,
                target_candidate_id: targetCandidateId,
            }),
        });
    } catch {
        return failure(UPLOAD_ERROR_MESSAGES.confirmConnectionLost, true);
    }

    if (!response) {
        return failure(UPLOAD_ERROR_MESSAGES.loginRequired, false);
    }

    if (!response.ok) {
        return classifyConfirmFailure(response);
    }

    // 成功応答の本文が読めない場合は、サーバで取込が済んでいる場合がある (No.8)
    const data = await readJsonOrNull(response);
    if (data === null) {
        return failure(UPLOAD_ERROR_MESSAGES.confirmConnectionLost, true);
    }
    return { ok: true, data };
}

/**
 * 求人プール一覧を 1 ページ取得する (T-001-U2、backend/app/routers/job_pool.py::list_job_pool_items)。
 * 業界 (industry) は画面から指定しない (画面レイアウト_SCR-M-001.md §6.4.3、Issue #779)。
 *
 * @param {string} targetCandidateId
 * @param {{skip?: number, limit?: number}} options
 * @returns {Promise<{ok: boolean, items: object[], total: number, errorMessage?: string}>}
 */
export async function fetchJobPoolItems(targetCandidateId, options = {}) {
    const params = new URLSearchParams();
    if (targetCandidateId) params.set('target_candidate_id', targetCandidateId);
    if (options.skip != null) params.set('skip', String(options.skip));
    if (options.limit != null) params.set('limit', String(options.limit));

    let response;
    try {
        response = await api.fetch(`/api/matching/job-pool?${params.toString()}`);
    } catch {
        return { ok: false, items: [], total: 0, errorMessage: '求人プールの取得に失敗しました。' };
    }

    if (!response) {
        return { ok: false, items: [], total: 0, errorMessage: 'ログインが必要です。' };
    }

    if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        return {
            ok: false,
            items: [],
            total: 0,
            errorMessage: userFacingDetail(response.status, errData) || '求人プールの取得に失敗しました。',
        };
    }

    const data = await response.json();
    return { ok: true, items: data.items || [], total: data.total || 0 };
}

// 求人プールからの削除の失敗の文言 (画面レイアウト_SCR-M-001.md §6.3「求人プールからの削除の失敗」の No. と対応、Issue #883)
export const JOB_POOL_DELETE_ERROR_MESSAGES = {
    loginRequired: 'ログインが必要です。',
    // No.1。消せる人に取り込んだ本人が加わった (Issue #782)
    forbidden: 'この求人を削除する権限がありません。求人を取り込んだ方、案件の担当者、またはマネージャーにご依頼ください。',
    notFound: 'この求人はすでに求人プールにありません。', // No.2
    network: '通信が途中で終了しました。削除されている場合があります。ページを再読み込みして確認してください。', // No.3
    failed: '求人を削除できませんでした。', // No.4 (detail が無い場合)
};

// ルートが無いときにサーバ (FastAPI) が返す 404 の detail。求人が無いときの 404 (日本語の detail) と分ける
const ROUTE_NOT_FOUND_DETAIL = 'Not Found';

/**
 * 求人プールから求人を 1 件削除する (画面レイアウト_SCR-M-001.md §6.2「求人プールからの削除（BTN-M-014）」、Issue #883)。
 * DELETE /api/matching/job-pool/{job_pool_item_id}。成功は 200 と { deleted_id }。
 * 入出力の正本は PBI-002 §11.10。
 *
 * 404 (もう無い) は、画面が削除できたときと同じ扱いにする (§6.3 No.2) ため、`notFound` で分けて返す。
 * ただしルートが無いときの 404 ("Not Found") は、その他の失敗にする (§6.3 No.4)。
 * 通信の失敗と、成功の応答を読めない・別の ID が返ったときは、削除が済んでいる場合があるため
 * 「削除されていない」と言わない文言にする (§6.3 No.3)。
 *
 * @param {string} jobPoolItemId
 * @returns {Promise<{ok: true} | {ok: false, notFound?: boolean, errorMessage: string}>}
 */
export async function deleteJobPoolItem(jobPoolItemId) {
    let response;
    try {
        response = await api.fetch(`/api/matching/job-pool/${encodeURIComponent(jobPoolItemId)}`, {
            method: 'DELETE',
        });
    } catch {
        return { ok: false, errorMessage: JOB_POOL_DELETE_ERROR_MESSAGES.network };
    }

    if (!response) {
        return { ok: false, errorMessage: JOB_POOL_DELETE_ERROR_MESSAGES.loginRequired };
    }

    if (response.status === 403) {
        return { ok: false, errorMessage: JOB_POOL_DELETE_ERROR_MESSAGES.forbidden };
    }
    if (response.status === 404) {
        // 「もう無い」はサーバが日本語の detail で返す 404 だけ (PBI-002 §11.10。§6.3 No.2)。
        // detail が "Not Found" (ルートが無い。削除の API の反映前のサーバ) か読めない 404 は、消えていないのに
        // 消えたように見せないため、その他の失敗にする (§6.3 No.4。PM の判断 #883 comment 6008263645 の ①)
        // detail は userFacingDetail を通して読む (サーバの detail を読む場所を 1 つに寄せる決まり。#1446)
        const detail = userFacingDetail(response.status, await readJsonOrNull(response));
        if (detail && detail !== ROUTE_NOT_FOUND_DETAIL) {
            return { ok: false, notFound: true, errorMessage: JOB_POOL_DELETE_ERROR_MESSAGES.notFound };
        }
        return { ok: false, errorMessage: JOB_POOL_DELETE_ERROR_MESSAGES.failed };
    }
    if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        return {
            ok: false,
            errorMessage: userFacingDetail(response.status, errData) || JOB_POOL_DELETE_ERROR_MESSAGES.failed,
        };
    }

    const data = await readJsonOrNull(response);
    if (data?.deleted_id !== jobPoolItemId) {
        return { ok: false, errorMessage: JOB_POOL_DELETE_ERROR_MESSAGES.network };
    }
    return { ok: true };
}

// 1 回に取得する件数。BE の limit の上限 (500) と揃える (§6.4.3、Issue #956)
export const JOB_POOL_PAGE_LIMIT = 500;

/**
 * 当該求職者の求人プールを全件取得する (画面レイアウト_SCR-M-001.md §6.4.3、Issue #779)。
 *
 * limit=500 で取得し、取れた件数が total に満たなければ skip を 500 ずつ進めて続けて取る。
 * 取得の途中に取込で先頭に 1 件増えると次のページで 1 件が重複しうるため、ID で重複を除く。
 * 途中のページで失敗した場合は、取れた分を返さず失敗として返す (一部だけを全件に見せない)。
 * ページを取っても新しい ID が増えなかった場合も、取得を終えて失敗として返す
 * (BE が skip を無視して同じページを返し続けると、取得を無限に繰り返すため)。
 *
 * @param {string} targetCandidateId
 * @returns {Promise<{ok: boolean, items: object[], total: number, errorMessage?: string}>}
 */
export async function fetchAllJobPoolItems(targetCandidateId) {
    const itemsById = new Map();
    let skip = 0;
    for (;;) {
        const page = await fetchJobPoolItems(targetCandidateId, { skip, limit: JOB_POOL_PAGE_LIMIT });
        if (!page.ok) return page;

        const sizeBefore = itemsById.size;
        page.items.forEach((item) => {
            if (!itemsById.has(item.id)) itemsById.set(item.id, item);
        });

        // 最後のページ (limit に満たない) か、total まで取れたら終える
        if (page.items.length < JOB_POOL_PAGE_LIMIT || itemsById.size >= page.total) {
            return { ok: true, items: Array.from(itemsById.values()), total: page.total };
        }
        // 続きがあるはずなのに新しい ID が増えないなら、取れた分は一部でしかない
        if (itemsById.size === sizeBefore) {
            return { ok: false, items: [], total: 0, errorMessage: '求人プールの取得に失敗しました。' };
        }
        skip += JOB_POOL_PAGE_LIMIT;
    }
}
